import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { PendingActionSummary, useAssistantApi } from '../../../common/hooks/use-assistant-api.hook';
import { CsAgentRun } from '../../../common/models/content-studio.model';
import { WaitingItem, groupWork } from './work-presentation';
import { useAgentWorkSignal } from './agent-work.store';

/** Live runs are re-read on this cadence; nothing else polls. */
const LIVE_POLL_MS = 5000;

/**
 * How long to keep watching after a run is asked for.
 *
 * A run is not always listed the instant it is created, so a single reload on
 * the signal can come back empty and leave nothing to poll for. Twenty seconds
 * is long enough for it to appear and short enough to be over before anyone
 * wonders why the tab is busy.
 */
const AFTER_START_MS = 20000;

export interface AgentWork {
    waiting: WaitingItem[];
    running: CsAgentRun[];
    done: CsAgentRun[];
    /** Runs stopped at their credit allowance, waiting on a yes or a no. */
    paused: CsAgentRun[];
    loading: boolean;
    failed: boolean;
    reload: () => Promise<void>;
}

/**
 * What the agents are doing, wherever it is being shown.
 *
 * Runs and pending proposals are two calls that are useless apart — a
 * proposal with no run to explain it, a run whose approval lives elsewhere —
 * so they are fetched together and grouped in one place. The Work page and
 * the dashboard widget read the same three lanes and cannot disagree about
 * what is running.
 *
 * Polls only while something is live, and stops the moment nothing is: a
 * quiet account should not be asking the API a question every five seconds
 * for as long as the tab is open.
 */
export function useAgentWork(limit = 50): AgentWork {
    const studio = useContentStudioApi();
    const assistant = useAssistantApi();

    const [runs, setRuns] = useState<CsAgentRun[]>([]);
    const [actions, setActions] = useState<PendingActionSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [r, a] = await Promise.all([studio.listAgentRuns({ limit }), assistant.listPendingActions()]);
            setRuns(r);
            setActions(a);
            setFailed(false);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const { waiting, running, done, paused } = useMemo(() => groupWork(runs, actions), [runs, actions]);

    useEffect(() => {
        if (!running.length) return undefined;
        const id = window.setInterval(() => void reload(), LIVE_POLL_MS);
        return () => window.clearInterval(id);
    }, [running.length, reload]);

    /*
     * Something just started a run somewhere else on the page.
     *
     * Watches briefly rather than reloading once, because the run may not be
     * listed yet. It stands down the moment one appears — the effect above
     * takes over from there, so the two never poll at the same time.
     */
    /*
     * Announcing that a run ended.
     *
     * This hook is the only thing polling the runs, so it is the only thing
     * that can notice. Compared by id rather than by array, because every poll
     * builds new objects and would otherwise look like a change.
     */
    const live = useRef<string[]>([]);
    useEffect(() => {
        const now = running.map((r) => r.runId);
        const ended = live.current.some((id) => !now.includes(id));
        live.current = now;
        if (ended) useAgentWorkSignal.getState().runFinished();
    }, [running]);

    const startedAt = useAgentWorkSignal((s) => s.startedAt);

    useEffect(() => {
        if (!startedAt || running.length) return undefined;

        void reload();
        const id = window.setInterval(() => void reload(), LIVE_POLL_MS);
        const stop = window.setTimeout(() => window.clearInterval(id), AFTER_START_MS);

        return () => {
            window.clearInterval(id);
            window.clearTimeout(stop);
        };
    }, [startedAt, running.length, reload]);

    return { waiting, running, done, paused, loading, failed, reload };
}
